import React, { useEffect } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Program } from 'models/user';

import Login from 'routes/Login';
import Documents from 'routes/Documents';
import Main from 'routes/Main';
import Normalizer from 'routes/Normalizer';
import PdfUploader from 'routes/PdfUploader';
import Report from 'routes/Report';
import Configurator from 'routes/Configurator';

import actions from 'state/actions';
import selectors from 'state/selectors';
import service from 'services';
import utils from 'utils';

import ModuleNotFound from 'components/ModuleNotFound';
import Layout from 'components/Layout';
import NotFound from 'components/NotFound';
import PleaseWait from 'components/PleaseWait';
import ControlTower from './ControlTower';
import DynamicWebComponent from './DynamicWebComponent/index';
import initModule from 'components/HOC/initModule';

const Routes = () => {
  // User login status from redux's store
  const userloginStatus = useSelector(selectors.user.getLoggedInStatus);
  // User's program list from redux's store
  const userPrograms = useSelector(selectors.user.getUserPrograms)?.programs;
  // User's idUser from redux's store
  const idUser = useSelector(selectors.user.getIdUser);

  const history = useHistory();

  const dispatch = useDispatch();

  // Set screen resolution
  useEffect(() => {
    const handleResize = () => {
      dispatch(actions.app.setScreenWidth(window.innerWidth));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch]);

  useEffect(() => {
    const getProgramsData = async (idUser: number) => {
      // menu programs elements
      const { data: menuPrograms } = await service.getMenuPrograms(idUser);

      dispatch(actions.user.transformAndSetMenuPrograms(menuPrograms));

      // programs
      const { data: programs } = await service.getUserPrograms(idUser);

      dispatch(actions.user.setUserPrograms(programs));
    };
    // userLoginStatus and idUser are valued after a valid login
    // and a dipatch to the performUserLogin redux action function
    // by the isUserLoggedIn method on this componet
    // or on the login page after a /login api call
    if (userloginStatus && idUser) getProgramsData(idUser);
  }, [userloginStatus, idUser, dispatch, history]);

  useEffect(() => {
    (async () => {
      // Checking if users has already logged by jwt token
      const user = await utils.user.isUserLoggedIn();
      if (!user) return history.push('/login');
      dispatch(actions.user.performUserLogin(user));

      dispatch(actions.chatbot.onUserLogin());
    })();
  }, [dispatch, history]);

  const renderProgram = (program: Program) => {
    if (program.moduleApp) {
      switch (program.moduleLink) {
        case 'VALIDATION': // should not be used anymore
        case 'DOCUMENTS': {
          return initModule(Documents, program.idProgram, program.programName);
        }
        case 'HUB': {
          return initModule(PdfUploader, program.idProgram, program.programName);
        }
        case 'NORMALIZER': {
          return initModule(Normalizer, program.idProgram, program.programName);
        }
        case 'REPORT': {
          return initModule(Report, program.idProgram, program.programName);
        }
        case 'CONFIGURATOR': {
          return initModule(Configurator, program.idProgram, program.programName);
        }
        case 'CONTROL_TOWER': {
          return initModule(ControlTower, program.idProgram, program.programName);
        }
        case 'EXTERNAL_APP': {
          return initModule(DynamicWebComponent, program.idProgram, program.programName, program.url ?? '');
        }
        default:
          return ModuleNotFound;
      }
    }
    return ModuleNotFound;
  };

  const walkthroughRefreshKey = useSelector(selectors.chatbot.getWalkthroughRefreshKey);

  return (
    <Switch>
      {userloginStatus && (
        <Layout>
          <Route exact path="/" component={Main} />
          {userPrograms?.map((program: Program, i) => {
            // HOC outside render
            // https://reactjs.org/docs/higher-order-components.html
            const Program = renderProgram(program);
            return (
              <Route
                render={(props) => <Program {...props} idProgram={program.idProgram} />}
                key={`${program.idProgram}-${i}-${walkthroughRefreshKey}`}
                path={`/${utils.converter.techName(program.programName)}`}
              />
            );
          })}
        </Layout>
      )}
      <Route path="/login" component={Login} />
      <Route path="*" component={userPrograms ? NotFound : PleaseWait} />
    </Switch>
  );
};

export default Routes;
